import { ArrowDown, ArrowUp } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";

import { Alert, Badge, Button, Card, CardTitle, Input, Label, NativeSelect, Tab, TabList } from "@realroof/ui";

import { api, canModerate, canSeeAudit } from "../api";
import { useSession } from "../session";
import { Table, td, th } from "../table";

export interface Place {
  id: string;
  kind: "city" | "town" | "locality";
  name: string;
  city_id: string | null;
  city: string | null;
  state: string;
  district: string | null;
  pincode: string | null;
  lat: number | null;
  lng: number | null;
  aliases: string[];
  area?: string | null; // localities: the village or suburb it lies in
  status: "active" | "hidden" | "merged" | string;
  source: string;
  is_top: boolean;
  top_order: number | null;
  hub_city_id: string | null;
  hub_city: string | null;
  merged_into: string | null;
  live_ads: number;
}

interface StateRow {
  state: string;
  cities: number;
  towns: number;
  top: number;
}

type TabKey = "suggested" | "top" | "cities" | "localities" | "add";
const TABS: [TabKey, string][] = [
  ["suggested", "Suggested by users"],
  ["top", "Top cities"],
  ["cities", "Cities & towns"],
  ["localities", "Localities"],
  ["add", "Add place"],
];

const find = (params: Record<string, string>) => api<Place[]>(`/admin/locations?${new URLSearchParams(params)}`);

/** Type a few letters, pick a city or town (used for localities and for adding a locality). */
export function CityChooser({ label, value, onChange }: { label: string; value: Place | null; onChange: (p: Place | null) => void }) {
  const [q, setQ] = useState("");
  const [options, setOptions] = useState<Place[]>([]);
  useEffect(() => {
    if (q.trim().length < 2) return setOptions([]);
    const t = setTimeout(() => {
      find({ q, limit: "8" })
        .then((list) => setOptions(list.filter((p) => p.kind !== "locality" && p.status === "active")))
        .catch(() => setOptions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  if (value) {
    return (
      <div className="flex items-center gap-2">
        <span>
          {label}: <strong>{value.name}</strong>, {value.state}
        </span>
        <Button variant="link" onClick={() => onChange(null)}>
          Change
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <Label>
        {label}
        <Input placeholder="Type a city or town" value={q} onChange={(e) => setQ(e.target.value)} />
      </Label>
      {options.length > 0 && (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={`${label} suggestions`}>
          {options.map((p) => (
            <Button key={p.id} size="sm" onClick={() => (onChange(p), setQ(""))}>
              {p.name}, {p.state}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Edit one place: names, aliases, PIN, point, hub (towns), top city (cities / towns), hide, merge (localities). */
function Editor({ place, canEdit, tops, onSaved, onClose }: { place: Place; canEdit: boolean; tops: Place[]; onSaved: (p: Place, note: string) => void; onClose: () => void }) {
  const [name, setName] = useState(place.name);
  const [aliases, setAliases] = useState(place.aliases.join(", "));
  const [pincode, setPincode] = useState(place.pincode ?? "");
  const [area, setArea] = useState(place.area ?? "");
  const [lat, setLat] = useState(place.lat?.toString() ?? "");
  const [lng, setLng] = useState(place.lng?.toString() ?? "");
  const [hub, setHub] = useState(place.hub_city_id ?? "");
  const [mergeQ, setMergeQ] = useState("");
  const [mergeOptions, setMergeOptions] = useState<Place[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (place.kind !== "locality" || mergeQ.trim().length < 2) return setMergeOptions([]);
    const t = setTimeout(() => {
      find({ q: mergeQ, kind: "locality", city_id: place.city_id ?? "", status: "active", limit: "8" })
        .then((list) => setMergeOptions(list.filter((p) => p.id !== place.id)))
        .catch(() => setMergeOptions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [mergeQ, place]);

  async function run(step: () => Promise<Place>, note: string) {
    setError("");
    try {
      onSaved(await step(), note);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  function save(e: FormEvent) {
    e.preventDefault();
    const body: Record<string, unknown> = {
      name: name.trim(),
      aliases: aliases.split(",").map((a) => a.trim()).filter(Boolean),
      pincode,
      lat: lat === "" ? null : Number(lat),
      lng: lng === "" ? null : Number(lng),
    };
    if (place.kind === "town" && !place.is_top) body.hub_city_id = hub;
    if (place.kind === "locality") body.area = area.trim();
    run(() => api<Place>(`/admin/locations/${place.id}`, { method: "PATCH", body }), `${name.trim()} saved; suggestions use it now.`);
  }

  return (
    <Card className="border-primary/40">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <CardTitle>
          {place.name} <span className="font-mono text-sm font-normal text-muted-foreground">{place.id}</span>
        </CardTitle>
        <Button variant="link" onClick={onClose}>
          Close
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        {place.kind}
        {place.city && place.kind === "locality" ? ` in ${place.city}` : ""} · {place.state} · {place.live_ads} live ads · source {place.source}
        {place.status !== "active" ? ` · ${place.status}` : ""}
      </p>
      {error && <Alert tone="error">{error}</Alert>}
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
        <fieldset disabled={!canEdit} className="contents">
          <Label>
            Name
            <Input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
          </Label>
          <Label>
            Aliases (comma separated: spellings, old names, short forms)
            <Input value={aliases} onChange={(e) => setAliases(e.target.value)} />
          </Label>
          <Label>
            PIN code
            <Input inputMode="numeric" maxLength={6} value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))} />
          </Label>
          {place.kind === "locality" && (
            <Label>
              Area (the village or suburb it lies in; shown as "{name || place.name}, {area || "Area"}, {place.city}")
              <Input value={area} maxLength={120} onChange={(e) => setArea(e.target.value)} />
            </Label>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Label>
              Latitude
              <Input inputMode="decimal" value={lat} onChange={(e) => setLat(e.target.value)} />
            </Label>
            <Label>
              Longitude
              <Input inputMode="decimal" value={lng} onChange={(e) => setLng(e.target.value)} />
            </Label>
          </div>
          {place.kind === "town" && !place.is_top && (
            <Label>
              Hub city (whose ads this town shows)
              <NativeSelect value={hub} onChange={(e) => setHub(e.target.value)}>
                <option value="">Nearest top city (automatic)</option>
                {tops.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </NativeSelect>
            </Label>
          )}
          {canEdit && (
            <div className="flex flex-wrap items-end gap-2 sm:col-span-2">
              <Button type="submit" variant="primary">
                Save
              </Button>
              {place.kind !== "locality" && place.status === "active" && (
                <Button onClick={() => run(() => api<Place>(`/admin/locations/${place.id}`, { method: "PATCH", body: { is_top: !place.is_top } }), place.is_top ? `${place.name} is no longer a top city.` : `${place.name} is a top city now (last in the picker).`)}>
                  {place.is_top ? "Remove from top cities" : "Make top city"}
                </Button>
              )}
              {place.status === "active" && (
                <Button variant="destructive" onClick={() => window.confirm(`Hide ${place.name}? It disappears from suggestions; posted ads keep it.`) && run(() => api<Place>(`/admin/locations/${place.id}/hide`, { method: "POST" }), `${place.name} is hidden.`)}>
                  Hide
                </Button>
              )}
              {place.status === "hidden" && (
                <Button onClick={() => run(() => api<Place>(`/admin/locations/${place.id}/unhide`, { method: "POST" }), `${place.name} is shown again.`)}>Unhide</Button>
              )}
            </div>
          )}
        </fieldset>
      </form>
      {canEdit && place.kind === "locality" && place.status === "active" && (
        <div className="flex flex-col gap-2 border-t border-border pt-3">
          <strong>Merge a duplicate</strong>
          <p className="text-sm text-muted-foreground">Keep the other locality; this one&apos;s ads move there and its name becomes an alias.</p>
          <Input aria-label="Merge into" placeholder={`Type the locality in ${place.city} to keep`} value={mergeQ} onChange={(e) => setMergeQ(e.target.value)} />
          <div className="flex flex-wrap gap-1.5">
            {mergeOptions.map((p) => (
              <Button key={p.id} size="sm" onClick={() => window.confirm(`Merge ${place.name} into ${p.name}? This cannot be undone here.`) && run(() => api<Place>(`/admin/locations/${place.id}/merge`, { method: "POST", body: { into_id: p.id } }), `${place.name} merged into ${p.name}.`)}>
                Keep {p.name}
              </Button>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function PlacesTable({ rows, onEdit }: { rows: Place[]; onEdit: (p: Place) => void }) {
  if (rows.length === 0) return <p className="text-muted-foreground">No places match.</p>;
  return (
    <Table>
      <thead>
        <tr>
          <th className={th}>Place</th>
          <th className={th}>Kind</th>
          <th className={th}>Hub / top</th>
          <th className={th}>Live ads</th>
          <th className={th} />
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => (
          <tr key={p.id}>
            <td className={td}>
              <strong>{p.name}</strong>
              {p.kind === "locality" && p.city ? <span className="text-muted-foreground">, {[p.area, p.city].filter(Boolean).join(", ")}</span> : null}
              <div className="text-xs text-muted-foreground">
                {p.id} · {p.state}
                {p.aliases.length ? ` · also: ${p.aliases.join(", ")}` : ""}
              </div>
            </td>
            <td className={td}>
              {p.kind}
              {p.status !== "active" && (
                <Badge tone="danger" className="ml-1.5">
                  {p.status}
                </Badge>
              )}
            </td>
            <td className={td}>{p.is_top ? <Badge tone="success">Top #{p.top_order}</Badge> : (p.hub_city ?? "—")}</td>
            <td className={td}>{p.live_ads}</td>
            <td className={td}>
              <Button size="sm" onClick={() => onEdit(p)} aria-label={`Edit ${p.name}`}>
                Edit
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** States & cities and localities (admin prototype). Changes reach the website's suggestions at once. */
export function Locations() {
  const { me } = useSession();
  const canEdit = canSeeAudit(me?.staff_role); // admins and super admins
  const [tab, setTab] = useState<TabKey>("top");
  const [tops, setTops] = useState<Place[]>([]);
  const [order, setOrder] = useState<Place[]>([]);
  const [states, setStates] = useState<StateRow[]>([]);
  const [state, setState] = useState("Telangana");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<Place[] | null>(null);
  const [city, setCity] = useState<Place | null>(null);
  const [editing, setEditing] = useState<Place | null>(null);
  const [message, setMessage] = useState<{ tone: "error" | "success"; text: string } | null>(null);

  const loadTops = () =>
    api<Place[]>("/admin/locations/top")
      .then((t) => {
        setTops(t);
        setOrder(t);
      })
      .catch((err: Error) => setMessage({ tone: "error", text: err.message }));
  useEffect(() => {
    loadTops();
    api<StateRow[]>("/admin/locations/states")
      .then(setStates)
      .catch(() => setStates([]));
  }, []);

  async function search(e?: FormEvent) {
    e?.preventDefault();
    setRows(null);
    try {
      if (tab === "cities") setRows((await find({ state, q, limit: "100" })).filter((p) => p.kind !== "locality"));
      if (tab === "localities") setRows(await find({ kind: "locality", q, limit: "100", ...(city ? { city_id: city.id } : {}) }));
    } catch (err) {
      setMessage({ tone: "error", text: (err as Error).message });
    }
  }
  useEffect(() => {
    setEditing(null);
    if (tab === "cities" || (tab === "localities" && city)) search();
    else setRows(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, state, city]);

  function saved(p: Place, note: string) {
    setMessage({ tone: "success", text: note });
    setEditing(p);
    setRows((list) => list?.map((r) => (r.id === p.id ? p : r)) ?? null);
    loadTops();
  }

  function move(i: number, d: -1 | 1) {
    const next = [...order];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setOrder(next);
  }

  /** Puts a city or town in the city picker (at the end), or takes it out. The place itself stays. */
  async function setTop(p: Place, top: boolean) {
    if (!top && !window.confirm(`Take ${p.name} out of the top cities? It stays on RealRoof; only the city picker changes.`)) return;
    setMessage(null);
    try {
      await api<Place>(`/admin/locations/${p.id}`, { method: "PATCH", body: { is_top: top } });
      setMessage({ tone: "success", text: top ? `${p.name} added to the top cities (at the end; move it up if you like).` : `${p.name} is no longer a top city.` });
      await loadTops();
    } catch (err) {
      setMessage({ tone: "error", text: (err as Error).message });
    }
  }

  async function saveOrder() {
    try {
      const done = await api<Place[]>("/admin/locations/top/order", { method: "PUT", body: { ids: order.map((p) => p.id) } });
      setTops(done);
      setOrder(done);
      setMessage({ tone: "success", text: "Order saved; the city picker shows it now." });
    } catch (err) {
      setMessage({ tone: "error", text: (err as Error).message });
    }
  }

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Locations</h1>
      <p className="text-sm text-muted-foreground">Top cities appear in the city picker. Small towns show their hub city&apos;s ads. Aliases make other spellings find a place.</p>
      <TabList>
        {TABS.filter(([k]) => k !== "add" || canEdit).map(([k, label]) => (
          <Tab key={k} selected={k === tab} className="flex-none" onClick={() => (setTab(k), setMessage(null))}>
            {label}
          </Tab>
        ))}
      </TabList>
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      {editing && <Editor key={editing.id + editing.status + editing.is_top} place={editing} canEdit={canEdit} tops={tops} onSaved={saved} onClose={() => setEditing(null)} />}

      {tab === "top" && (
        <Card>
          <ol className="m-0 flex list-none flex-col gap-1 p-0">
            {order.map((p, i) => (
              <li key={p.id} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted">
                <span className="w-6 text-right font-mono text-sm text-muted-foreground">{i + 1}</span>
                <span className="flex-1">
                  <strong>{p.name}</strong> <span className="text-sm text-muted-foreground">· {p.state} · {p.live_ads} live ads</span>
                </span>
                {canEdit && (
                  <>
                    <Button size="icon" variant="ghost" aria-label={`Move ${p.name} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                      <ArrowUp aria-hidden="true" className="size-4" />
                    </Button>
                    <Button size="icon" variant="ghost" aria-label={`Move ${p.name} down`} disabled={i === order.length - 1} onClick={() => move(i, 1)}>
                      <ArrowDown aria-hidden="true" className="size-4" />
                    </Button>
                  </>
                )}
                <Button size="sm" onClick={() => setEditing(p)} aria-label={`Edit ${p.name}`}>
                  Edit
                </Button>
                {canEdit && (
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setTop(p, false)} aria-label={`Remove ${p.name} from top cities`}>
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ol>
          {canEdit && order.length === tops.length && (
            <div className="border-t border-border pt-3">
              <CityChooser label="Add a top city" value={null} onChange={(p) => p && (p.is_top ? setMessage({ tone: "error", text: `${p.name} is already a top city.` }) : setTop(p, true))} />
              <p className="m-0 mt-1 text-xs text-muted-foreground">A city or town not on RealRoof yet? Add it first under Add place.</p>
            </div>
          )}
          {canEdit && (
            <Button variant="primary" className="self-start" disabled={order.map((p) => p.id).join() === tops.map((p) => p.id).join()} onClick={saveOrder}>
              Save order
            </Button>
          )}
        </Card>
      )}

      {tab === "cities" && (
        <form onSubmit={search} className="flex flex-wrap items-end gap-2">
          <Label>
            State
            <NativeSelect value={state} onChange={(e) => setState(e.target.value)}>
              {states.map((s) => (
                <option key={s.state} value={s.state}>
                  {s.state} ({s.cities + s.towns})
                </option>
              ))}
            </NativeSelect>
          </Label>
          <Input className="max-w-xs" aria-label="Search cities and towns" placeholder="Name or alias" value={q} onChange={(e) => setQ(e.target.value)} />
          <Button type="submit">Search</Button>
        </form>
      )}
      {tab === "localities" && (
        <div className="flex flex-col gap-3">
          <CityChooser label="City" value={city} onChange={setCity} />
          {city && (
            <form onSubmit={search} className="flex flex-wrap gap-2">
              <Input className="max-w-xs" aria-label="Search localities" placeholder="Locality name or alias" value={q} onChange={(e) => setQ(e.target.value)} />
              <Button type="submit">Search</Button>
            </form>
          )}
        </div>
      )}
      {(tab === "cities" || tab === "localities") && rows === null && (tab === "cities" || city) && <p className="text-muted-foreground">Loading…</p>}
      {(tab === "cities" || tab === "localities") && rows && <PlacesTable rows={rows} onEdit={setEditing} />}

      {tab === "suggested" && <Suggestions canDecide={canModerate(me?.staff_role)} onEdit={setEditing} />}

      {tab === "add" && canEdit && <AddPlace states={states} onAdded={(p) => (setMessage({ tone: "success", text: `${p.name} added (${p.id}); people can find it now.` }), setEditing(p))} />}
    </section>
  );
}

function AddPlace({ states, onAdded }: { states: StateRow[]; onAdded: (p: Place) => void }) {
  const [kind, setKind] = useState<"locality" | "town" | "city">("locality");
  const [name, setName] = useState("");
  const [city, setCity] = useState<Place | null>(null);
  const [state, setState] = useState("Telangana");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [pincode, setPincode] = useState("");
  const [aliases, setAliases] = useState("");
  const [error, setError] = useState("");

  async function add(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      const place = await api<Place>("/admin/locations", {
        method: "POST",
        body: {
          kind,
          name: name.trim(),
          ...(kind === "locality" ? { city_id: city?.id } : { state }),
          lat: Number(lat),
          lng: Number(lng),
          pincode: pincode || null,
          aliases: aliases.split(",").map((a) => a.trim()).filter(Boolean),
        },
      });
      setName("");
      setAliases("");
      onAdded(place);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Card>
      <form onSubmit={add} className="grid gap-3 sm:grid-cols-2">
        <Label>
          Kind
          <NativeSelect value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="locality">Locality / colony / area</option>
            <option value="town">Town</option>
            <option value="city">City</option>
          </NativeSelect>
        </Label>
        <Label>
          Name
          <Input required minLength={2} value={name} onChange={(e) => setName(e.target.value)} />
        </Label>
        <div className="sm:col-span-2">
          {kind === "locality" ? (
            <CityChooser label="In city" value={city} onChange={setCity} />
          ) : (
            <Label>
              State
              <NativeSelect value={state} onChange={(e) => setState(e.target.value)}>
                {states.map((s) => (
                  <option key={s.state}>{s.state}</option>
                ))}
              </NativeSelect>
            </Label>
          )}
        </div>
        <Label>
          Latitude
          <Input required inputMode="decimal" placeholder="17.4521" value={lat} onChange={(e) => setLat(e.target.value)} />
        </Label>
        <Label>
          Longitude
          <Input required inputMode="decimal" placeholder="78.2412" value={lng} onChange={(e) => setLng(e.target.value)} />
        </Label>
        <Label>
          PIN code (optional)
          <Input inputMode="numeric" maxLength={6} value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))} />
        </Label>
        <Label>
          Aliases (optional, comma separated)
          <Input value={aliases} onChange={(e) => setAliases(e.target.value)} />
        </Label>
        {error && <Alert tone="error" className="sm:col-span-2">{error}</Alert>}
        <Button type="submit" variant="primary" className="self-start" disabled={kind === "locality" && !city}>
          Add place
        </Button>
      </form>
    </Card>
  );
}

/** Colonies users could not find while posting, oldest first. Usually decided on the ad's review page. */
function Suggestions({ canDecide, onEdit }: { canDecide: boolean; onEdit: (p: Place) => void }) {
  const [rows, setRows] = useState<(Place & { landmark?: string | null })[] | null>(null);
  const [message, setMessage] = useState<{ tone: "error" | "success"; text: string } | null>(null);
  const load = () =>
    find({ status: "suggested", limit: "200" })
      .then(setRows)
      .catch((err: Error) => setMessage({ tone: "error", text: err.message }));
  useEffect(() => {
    load();
  }, []);

  async function decide(p: Place, path: "approve" | "reject") {
    if (path === "reject" && !window.confirm(`Reject "${p.name}"? Ads using it lose their locality.`)) return;
    try {
      await api(`/admin/locations/${p.id}/${path}`, { method: "POST", body: {} });
      setMessage({ tone: "success", text: path === "approve" ? `${p.name} is added; everyone can find it now.` : `${p.name} rejected.` });
      await load();
    } catch (err) {
      setMessage({ tone: "error", text: (err as Error).message });
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      {rows?.length === 0 && <p className="text-muted-foreground">No suggestions waiting.</p>}
      {rows && rows.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th className={th}>Suggested locality</th>
              <th className={th}>Ads using it</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="bg-premium-soft/40">
                <td className={td}>
                  <strong>{p.name}</strong>, {p.city}
                  <div className="text-xs text-muted-foreground">
                    {p.id}
                    {p.lat != null ? ` · ${p.lat}, ${p.lng}` : ""}
                  </div>
                </td>
                <td className={td}>{p.live_ads}</td>
                <td className={td}>
                  <div className="flex flex-wrap gap-1.5">
                    {canDecide && (
                      <>
                        <Button size="sm" variant="primary" onClick={() => decide(p, "approve")}>
                          Approve
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => decide(p, "reject")}>
                          Reject
                        </Button>
                      </>
                    )}
                    <Button size="sm" onClick={() => onEdit(p)} aria-label={`Edit ${p.name}`}>
                      Edit / merge
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <p className="text-xs text-muted-foreground">Tip: the ad review page shows similar existing localities, so you can map a suggestion to one of them.</p>
    </div>
  );
}
